// Small "ding" using the Web Audio API — no external asset needed.
// Respects the user preference stored in localStorage.helios_notif_sound (default: enabled).
// Optional custom sound: localStorage.helios_notif_sound_url can hold a data-URL (mp3/wav/ogg).

const KEY = "helios_notif_sound";
const URL_KEY = "helios_notif_sound_url";
const NAME_KEY = "helios_notif_sound_name";
const URL_KEY_URGENT = "helios_notif_sound_url_urgent";
const NAME_KEY_URGENT = "helios_notif_sound_name_urgent";

export const isNotificationSoundEnabled = () => {
  try { return localStorage.getItem(KEY) !== "false"; } catch { return true; }
};

export const setNotificationSoundEnabled = (on) => {
  try { localStorage.setItem(KEY, on ? "true" : "false"); } catch { /* ignore */ }
};

export const getCustomSound = (variant = "standard") => {
  const isUrgent = variant === "urgent";
  try {
    return {
      url: localStorage.getItem(isUrgent ? URL_KEY_URGENT : URL_KEY) || "",
      name: localStorage.getItem(isUrgent ? NAME_KEY_URGENT : NAME_KEY) || "",
    };
  } catch { return { url: "", name: "" }; }
};

export const setCustomSound = (url, name, variant = "standard") => {
  const isUrgent = variant === "urgent";
  const uk = isUrgent ? URL_KEY_URGENT : URL_KEY;
  const nk = isUrgent ? NAME_KEY_URGENT : NAME_KEY;
  try {
    if (url) { localStorage.setItem(uk, url); localStorage.setItem(nk, name || ""); }
    else { localStorage.removeItem(uk); localStorage.removeItem(nk); }
  } catch { /* ignore */ }
};

let ctx = null;
const getCtx = () => {
  if (!ctx) {
    try {
      const AC = window.AudioContext || window.webkitAudioContext;
      if (AC) ctx = new AC();
    } catch { /* ignore */ }
  }
  return ctx;
};

const playSynthDing = (urgent = false) => {
  const c = getCtx();
  if (!c) return;
  try {
    if (c.state === "suspended") c.resume().catch(() => {});
    const now = c.currentTime;
    // Urgent: 3 sharp high tones (like an alarm). Standard: soft 2-tone chime.
    const tones = urgent
      ? [
          { freq: 1568, start: 0.00, dur: 0.10 }, // G6
          { freq: 1568, start: 0.14, dur: 0.10 },
          { freq: 1976, start: 0.28, dur: 0.14 }, // B6
        ]
      : [
          { freq: 880, start: 0.00, dur: 0.12 },  // A5
          { freq: 1318, start: 0.10, dur: 0.16 }, // E6
        ];
    for (const t of tones) {
      const osc = c.createOscillator();
      const gain = c.createGain();
      osc.type = urgent ? "triangle" : "sine";
      osc.frequency.setValueAtTime(t.freq, now + t.start);
      gain.gain.setValueAtTime(0.0001, now + t.start);
      gain.gain.exponentialRampToValueAtTime(urgent ? 0.30 : 0.22, now + t.start + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, now + t.start + t.dur);
      osc.connect(gain).connect(c.destination);
      osc.start(now + t.start);
      osc.stop(now + t.start + t.dur + 0.02);
    }
  } catch { /* ignore */ }
};

/** Play the notification sound.
 *  Pass `{ urgent: true }` for critique/urgente tickets → uses urgent custom sound or a sharper synth. */
export const playNotificationDing = (opts = {}) => {
  if (!isNotificationSoundEnabled()) return;
  const urgent = !!opts.urgent;
  const { url } = getCustomSound(urgent ? "urgent" : "standard");
  // Fallback: if urgent variant not uploaded, fall back to standard custom sound
  const effectiveUrl = url || (urgent ? getCustomSound("standard").url : "");
  if (effectiveUrl) {
    try {
      const a = new Audio(effectiveUrl);
      a.volume = urgent ? 0.9 : 0.7;
      a.play().catch(() => playSynthDing(urgent));
      return;
    } catch { /* fall through to synth */ }
  }
  playSynthDing(urgent);
};
